import { Component, inject, OnChanges, ChangeDetectionStrategy, input, output } from '@angular/core';
import { GuruEntity } from '../../../model/guru-entity';
import { UnitRatingResponse } from '../../../model/unit-rating-response';
import { ModelService } from '../../../services/model-service';
import { UserStateService, ConfigurationSection } from '../../../services/user-state.service';
import { SelectionUtils } from '../../selection-utils';
import { UnitConversionUtils } from '../../unit-conversion-utils';
import { FormsModule } from '@angular/forms';
import { PerformanceInputComponent } from '../../common/performance-input/performance-input.component';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-air-handler-unit-performance-input',
  imports: [FormsModule, PerformanceInputComponent, MatIcon],
  templateUrl: './air-handler-unit-performance-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './air-handler-unit-performance-input.component.css',
})
export class AirHandlerUnitPerformanceInputComponent implements OnChanges {
  private modelService = inject(ModelService);
  private userStateService = inject(UserStateService);
  readonly invalidEntities = input.required<string[]>();
  readonly engineKey = input.required<string>();
  readonly selectionEntities = input.required<GuruEntity[]>();
  readonly ratingResponse = input.required<UnitRatingResponse>();
  readonly valueChanged = output<void>();
  readonly inputUnitsChanged = output<void>();
  airflowSelection!: GuruEntity;
  staticPressureTypeSelection!: GuruEntity;
  staticPressureValueSelection!: GuruEntity;
  altitudeSelection!: GuruEntity;
  inputTypeSelection!: GuruEntity;
  isExpanded = true;
  isMetric = false;
  ratingResponseError!: string;
  private sectionKey = ConfigurationSection.PERFORMANCE_INPUTS;

  constructor() {
    this.isExpanded = !this.userStateService.isConfigurationSectionCollapsed(this.sectionKey);
  }

  ngOnChanges(): void {
    this.airflowSelection = SelectionUtils.getEntity(this.selectionEntities(), 'USFAirflow');
    this.staticPressureTypeSelection = SelectionUtils.getEntity(this.selectionEntities(), 'USFStatic');
    this.staticPressureValueSelection = SelectionUtils.getEntity(this.selectionEntities(), 'USFESP');
    this.altitudeSelection = SelectionUtils.getEntity(this.selectionEntities(), 'USAltitude');
    this.inputTypeSelection = SelectionUtils.getEntity(this.selectionEntities(), 'USMetricInput');
    this.isMetric = this.inputTypeSelection && 'true' === this.inputTypeSelection.Value;
    if (this.ratingResponse()) {
      this.ratingResponseError = this.ratingResponse().ErrorMessage;
    }
  }

  selectionChanged(entity: GuruEntity, newValue: string): void {
    this.modelService.setValue(entity.Name, this.engineKey(), newValue).subscribe(() => this.valueChanged.emit());
  }

  toggleExpansion(): void {
    this.isExpanded = !this.isExpanded;
    this.userStateService.setCollapsedValue(this.sectionKey, this.isExpanded);
  }

  getExpansionIcon(): string {
    return this.isExpanded ? 'arrow_drop_up' : 'arrow_drop_down';
  }

  isInvalidSelection(entityName: string) {
    return this.invalidEntities() && this.invalidEntities().indexOf(entityName) >= 0;
  }

  getPressureMeasurementUnits(): string {
    return UnitConversionUtils.getEspUnits(this.isMetric);
  }

  getDistanceMeasurementUnits(): string {
    return UnitConversionUtils.getLengthUnits(this.isMetric);
  }

  getAirflowMeasurementUnits(): string {
    return UnitConversionUtils.getAirflowUnits(this.isMetric);
  }

  inputTypeChanged(newValue: string): void {
    this.modelService.setValue(this.inputTypeSelection.Name, this.engineKey(), newValue).subscribe(() => this.inputUnitsChanged.emit());
  }

  performanceValueChanged(): void {
    this.valueChanged.emit();
  }
}
